/**
 * NOTE: Some forced reflows may also come from third-party scripts:
 * - uploadly.js (uploadly-cdn.com)
 * - Klaviyo scripts (static.klaviyo.com)
 * - Microsoft Clarity (clarity.js)
 * - vendor.min.js (contains bundled third-party libraries like lazySizes, Flickity)
 * 
 * These must be configured or disabled via their own settings, not edited here in theme JS.
 * vendor.min.js should be optimized at build time if it contains our theme code.
 */

if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      // Cache layout values to avoid forced reflows
      this.cachedStickyOffset = 0;
      this.cachedHeaderHeight = 0;
      this.cachedHeaderOffset = 0;
      this.cachedAnnouncementHeight = 0;
      this.lastKnownScrollY = 0;
      this.ticking = false;
      this.announcementBar = null;
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');
      this.announcementBar = document.querySelector('.announcement-bar-section');

      // Initialize cached values
      this.recalculateLayoutValues();

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      });

      // Recalculate layout values on resize (throttled)
      window.addEventListener('resize', debounce(() => {
        this.recalculateLayoutValues();
      }, 100), { passive: true });

      // Batch scroll handlers with requestAnimationFrame
      window.addEventListener('scroll', () => {
        this.lastKnownScrollY = window.scrollY;
        if (!this.ticking) {
          window.requestAnimationFrame(() => {
            this.updateOnScroll(this.lastKnownScrollY);
            this.ticking = false;
          });
          this.ticking = true;
        }
      }, { passive: true });

      // Initial update
      window.dispatchEvent(new Event('scroll'));

      if (this.announcementBar) {
        window.dispatchEvent(new Event('resize'));
      }

      // Mobile Navigation transparent header support.
      setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }
    recalculateLayoutValues() {
      // READ layout values once, cache them
      if (this.classList.contains('header-sticky--active')) {
        this.cachedStickyOffset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }
      if (this.header_section) {
        this.cachedHeaderOffset = this.header_section.getBoundingClientRect().top;
      }
      this.cachedHeaderHeight = this.clientHeight;
      if (this.announcementBar) {
        this.cachedAnnouncementHeight = this.announcementBar.clientHeight;
      }
    }
    updateOnScroll(scrollY) {
      // Use cached values, no layout reads during scroll
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', scrollY >= this.cachedStickyOffset && scrollY > 0);
      }
      if (this.header_section) {
        document.documentElement.style.setProperty('--header-offset', this.cachedHeaderOffset + 'px');
      }
      document.documentElement.style.setProperty('--header-height', this.cachedHeaderHeight + 'px');
      if (this.announcementBar) {
        document.documentElement.style.setProperty('--announcement-height', this.cachedAnnouncementHeight + 'px');
      }
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function() {
        window.addEventListener('resize', debounce(function() {
          _this.resizeSubMenus();
        }, 100));
      });

    }
    resizeSubMenus() {
      // Cache window.innerWidth to avoid repeated reads
      const windowWidth = window.innerWidth;
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          // Batch layout reads together
          const parentEl = sub_submenu.parentElement;
          const parentRect = parentEl.getBoundingClientRect();
          const w = sub_submenu.offsetWidth;
          const l = parentRect.left + parentEl.offsetWidth;
          const total = w + l;

          if (total > windowWidth) {
            parentEl.classList.add('left-submenu');
          } else if (parentEl.classList.contains('left-submenu')) {
            parentEl.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}